import React, { useState } from 'react'
import { Tooltip, InputNumber, message } from 'antd'
import { MyModal } from '~/components/ui/MyModal'
import { MyImage } from '~/components/ui/MyImage'
import { InfoCard, InfoCardItem } from '~/components/pages/product/InfoCard'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { MIN_PRICE, MAX_PRICE } from '~/configs'
import { MyForm } from '../ui/MyForm'
import {
  FenToYuan,
  graphQLErrorCode,
  graphQLErrorMessage,
  isAmount,
  YuanToFen,
} from '~/utils'
import _ from 'lodash'
import { MyDivide } from '~/components/ui/MyDivide'
import { MyBtn } from '../ui/MyBtn'
import { useCountDown } from '~/hooks/useCountDown'
import { GetProductResultModal } from './GetProductResultModal'
import {
  waitGetProduct_itemModule_waitGetArtwork,
  ArtworkSendType,
  preSettlement_settlementModule_copyrightPreSettlement,
  preSettlement,
  preSettlementVariables,
  ArtworkSendStatus,
  claimProduct,
  claimProductVariables,
  claimProduct_itemModule_userGetArtwork,
} from '~/graphqls/types'
import { useMutation } from '@apollo/client'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { CLAIM_PRODUCT, PRE_SETTLEMENT } from '~/graphqls/gqls'
import Cookies from 'js-cookie'

export const GetProductDetailModal: React.FC<{
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
  data: waitGetProduct_itemModule_waitGetArtwork
}> = ({ visible, setVisible, data }) => {
  const [resultVisible, setResultVisible] = useState(false)
  const [loading, setLoading] = useState(false)

  const [productData, setProductData] =
    useState<claimProduct_itemModule_userGetArtwork>()

  const { days, hours, minutes, seconds } = useCountDown(data.endTimeDiff || 0)

  const [claimProductAction] = useMutation<claimProduct, claimProductVariables>(
    CLAIM_PRODUCT
  )

  const isAirDrop =
    data.type === ArtworkSendType.BLIND_BOX ||
    data.type === ArtworkSendType.CHOICE

  const isExpire = data.status === ArtworkSendStatus.CANCEL

  const expireToast = () => {
    MyModal.info({
      title: isAirDrop ? '礼物领取已过期' : '提示',
      content: isAirDrop
        ? '抱歉，空投礼物领取时间已截止，已自动回收，不再进行二次补送，请您谅解。'
        : '预售作品已被领取',
      icon: '',
      maskStyle: {
        backgroundColor: 'rgba(0,0,0,0.8)',
      },
      width: 300,
      okText: '知道了',
      onOk: close => {
        close()
        Cookies.set('hidden-get-product-modal', String(true), {
          expires: new Date(new Date().getTime() + 2 * 60 * 60 * 1000),
        })
        location.reload()
      },
    })
  }

  const onClaim = async () => {
    if (isExpire) {
      expireToast()
      return
    }
    setLoading(true)

    claimProductAction({
      variables: {
        productId: data.product?.id,
        usePrice: YuanToFen(usePrice) || 0,
        changePrice: YuanToFen(adaptPrice) || 0,
      },
    })
      .then(res => {
        setProductData(
          res.data?.itemModule
            ?.userGetArtwork as claimProduct_itemModule_userGetArtwork
        )
        setResultVisible(true)
      })
      .catch(err => {
        const code = graphQLErrorCode(err)
        // 空投过期
        if (code === 20060000) {
          expireToast()
        } else {
          message.error(graphQLErrorMessage(err) || '领取失败')
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const [usePrice, setUsePrice] = useState<number | undefined>(undefined)
  const [adaptPrice, setAdaptPrice] = useState<number | undefined>(undefined)

  return (
    <MyModal
      title="领取详情"
      visible={visible}
      onCancel={() => {
        setVisible(false)
        location.reload()
      }}
      maskStyle={{
        backgroundColor: 'rgba(0,0,0,0.8)',
      }}
      width={448}
      footer={null}
    >
      {isAirDrop && (
        <div className="flex justify-between items-center bg-neutral-700 rounded-2xl p-3 mb-6">
          <span>领取倒计时</span>
          <span className="flex justify-center items-center">
            <span className="mr-2">
              {Math.floor(days) ? `${Math.floor(days)}天` : ''}
            </span>
            <span className="bg-neutral-200 text-neutral-800 w-8 h-8 leading-8  inline-block rounded-lg text-center">
              {hours <= 9 ? `0${hours}` : hours}
            </span>
            <span className="mx-2">:</span>
            <span className="bg-neutral-200 text-neutral-800 w-8 h-8 leading-8  inline-block rounded-lg text-center">
              {minutes <= 9 ? `0${minutes}` : minutes}
            </span>
            <span className="mx-2">:</span>
            <span className="bg-neutral-200 text-neutral-800 w-8 h-8 leading-8  inline-block rounded-lg text-center">
              {seconds <= 9 ? `0${seconds}` : seconds}
            </span>
          </span>
        </div>
      )}

      <div className="flex items-center mb-4">
        <MyImage
          tag="v400"
          src={data.product?.artworkUrl}
          style={{ width: 72, height: 72, borderRadius: 16 }}
          lazy={false}
        />
        <div className="ml-4">
          <div className="text-sm text-neutral-100 mb-1 font-medium">
            {data.product?.name}
          </div>
          <div className="text-xs text-neutral-400">
            版权所有权：
            <span className="text-error font-semibold">
              {isAirDrop
                ? '¥-'
                : `¥${FenToYuan(data.product?.buy?.[0]?.price)}`}
            </span>
          </div>
        </div>
      </div>
      <MyForm
        layout="vertical"
        initialValues={{
          useprice: usePrice,
          adaptprice: adaptPrice,
        }}
      >
        <MyForm.Item
          label={
            <>
              标准类授权（使用权）价格
              <Tooltip
                placement="topLeft"
                title="不同使用权价格之间的比例关系，标准类授权：拓展类授权：商品类授权）=1:1.5:3，其他使用权价格将根据标准类授权价格自动计算出出售价格"
              >
                <QuestionCircleOutlined style={{ marginLeft: 3 }} />
              </Tooltip>
            </>
          }
          name="useprice"
          rules={[
            {
              type: 'number',
              validator(_rule, value) {
                if (!value) {
                  return Promise.reject('请输入使用权价格')
                } else if (value < 1) {
                  return Promise.reject(`使用权价格需大于¥${1}`)
                } else if (value > 1000) {
                  return Promise.reject(`使用权价格需小于¥${1000}`)
                } else if (!isAmount.test(value)) {
                  return Promise.reject('价格最多支持两位小数')
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <InputNumber
            controls={false}
            addonAfter="CNY"
            value={usePrice}
            onChange={v => {
              setUsePrice(v || 0)
            }}
            placeholder="请输入使用权价格"
          />
        </MyForm.Item>

        {usePrice && (
          <InfoCard style={{ marginBottom: 24 }}>
            <InfoCardItem label="作品使用权价格"></InfoCardItem>
            <InfoCardItem label="标准类授权">
              ¥ {usePrice.toFixed(2)}
            </InfoCardItem>
            <InfoCardItem label="扩展类授权">
              ¥ {(usePrice * 1.5).toFixed(2)}
            </InfoCardItem>
            <InfoCardItem label="商品类授权">
              ¥ {(usePrice * 3).toFixed(2)}
            </InfoCardItem>
          </InfoCard>
        )}

        <MyForm.Item
          label={<>改编权价格</>}
          name="adaptprice"
          rules={[
            {
              type: 'number',
              validator(_rule, value) {
                if (!value) {
                  return Promise.reject('请输入改编权价格')
                } else if (value < 1) {
                  return Promise.reject(`改编权价格需大于¥1`)
                } else if (value > 1000) {
                  return Promise.reject(`改编权价格需小于¥1000`)
                } else if (!isAmount.test(value)) {
                  return Promise.reject('价格最多支持两位小数')
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <InputNumber
            controls={false}
            addonAfter="CNY"
            value={adaptPrice}
            onChange={v => {
              setAdaptPrice(v || 0)
            }}
            placeholder="请输入改编权价格"
          />
        </MyForm.Item>
      </MyForm>

      <div className="text-xs text-neutral-400 mb-6 mt-6">
        规则说明： <br />
        {isAirDrop ? (
          <>
            1.因平台规则设置，领取版权作品时，需同时设置二次出售价格，领取成功后，作品将直接进入版权交易市场，请合理设置二次出售价格，平台严厉杜绝作品炒作、违规交易等行为。
            <br />
            2.请在规定时间内及时领取空投奖励，逾期视为自愿放弃，空投届时将自动回收，不再进行二次补送。
          </>
        ) : (
          <>领取预售作品时需要设置出售价格，领取成功后直接进入版权交易市场。</>
        )}
      </div>
      <MyDivide space={12} />
      <MyBtn onClick={onClaim} loading={loading} type="primary" block>
        立即领取
      </MyBtn>
      {resultVisible && (
        <GetProductResultModal
          visible={resultVisible}
          setVisible={setResultVisible}
          data={productData}
        />
      )}
    </MyModal>
  )
}
